/**
 * The widget's trace model (spec-langchain-native-runtime 08c). Pure functions only.
 *
 * Chat (08a) sends a protocol-2 widget a LABELS-ONLY projection of the agent's trace as a `trace`
 * field on `record_update_stream` (no `chunk` / `done` on those messages):
 *   { kind: "status",  label: "thinking" | "working" }                       reasoning / hand-off
 *   { kind: "step",    id, label, status: "running"|"done"|"failed", durationMs? }  a labelled tool
 *   { kind: "summary", steps, label: null }                                   after `done`, steps ≥ 1
 * On the history read the persisted summary is already projected to `{ steps, label: null }`.
 *
 * Everything here copies WHITELISTED fields only: a tool name, arguments, a result, reasoning text
 * or an agent path on an event never reaches state, so it can never be rendered. An event this
 * widget does not understand (a new kind, a malformed field) leaves the state unchanged (D-L66).
 */

export const TRACE_STATUS_KEYS = Object.freeze(["thinking", "working"]);
export const TRACE_STEP_STATUSES = Object.freeze(["running", "done", "failed"]);
/** Chat caps labels at 200 characters; the widget applies the same bound. */
export const TRACE_LABEL_MAX = 200;
/** A runaway turn must not grow the bubble without bound (chat caps the summary at 50 tools). */
export const TRACE_STEPS_MAX = 50;

const isPlainObject = (v) => v !== null && typeof v === "object" && !Array.isArray(v);
const nonBlank = (v) => (typeof v === "string" && v.trim() !== "" ? v.trim() : null);
const positiveInt = (v) => (Number.isInteger(v) && v > 0 ? v : null);

/** The empty trace of a record: `off` until the first understood trace event arrives. */
export const emptyTrace = () => ({ status: null, steps: [], summary: null, off: true });

/**
 * The status key a `status` event maps to. Chat sends keys, not copy; an unknown key renders as the
 * neutral "working" line rather than as server text.
 */
export const toStatusKey = (label) => (label === "thinking" ? "thinking" : "working");

/**
 * The customer-safe summary `{ steps, label: null }` from any shape the widget can meet, or null:
 *  - the live `summary` event / the projected history field — `{ steps }`;
 *  - a full persisted summary `{ tools: [{ name, label, … }] }` that reached the widget unprojected
 *    — counted by its LABELLED tools only (what the customer would have seen), names dropped.
 * The server's `label` is never used: the widget renders its own localized copy.
 */
export function normalizeTraceSummary(raw) {
	if (!isPlainObject(raw)) return null;
	const steps = positiveInt(raw.steps);
	if (steps !== null) return { steps, label: null };
	if (Array.isArray(raw.tools)) {
		const labelled = raw.tools.filter((t) => isPlainObject(t) && nonBlank(t.label)).length;
		return labelled > 0 ? { steps: labelled, label: null } : null;
	}
	return null;
}

/** A `step` event reduced to the fields the widget may keep, or null when it is not a valid step. */
export function toStep(ev) {
	if (!isPlainObject(ev)) return null;
	const id = nonBlank(typeof ev.id === "number" ? String(ev.id) : ev.id);
	const label = nonBlank(ev.label);
	if (!id || !label || !TRACE_STEP_STATUSES.includes(ev.status)) return null;
	const step = { id, label: label.slice(0, TRACE_LABEL_MAX), status: ev.status };
	if (Number.isFinite(ev.durationMs) && ev.durationMs >= 0) step.durationMs = ev.durationMs;
	return step;
}

/** Whether a `trace` payload is one this widget understands (and so may change state). */
export function isKnownTraceEvent(ev) {
	if (!isPlainObject(ev)) return false;
	if (ev.kind === "status") return nonBlank(ev.label) !== null;
	if (ev.kind === "step") return toStep(ev) !== null;
	if (ev.kind === "summary") return normalizeTraceSummary(ev) !== null;
	return false;
}

/**
 * Fold one `trace` event into a record's trace state. Unknown or malformed events return `prev`
 * itself (same reference), so callers can tell nothing changed.
 * @param {{status: string|null, steps: object[], summary: object|null, off: boolean}} prev
 * @param {object} ev
 */
export function reduceTrace(prev, ev) {
	const base = isPlainObject(prev) ? prev : emptyTrace();
	if (!isKnownTraceEvent(ev)) return prev;
	if (ev.kind === "status") return { ...base, status: toStatusKey(ev.label), off: false };
	if (ev.kind === "summary") return { ...base, summary: normalizeTraceSummary(ev), off: false };
	const step = toStep(ev);
	const steps = Array.isArray(base.steps) ? base.steps : [];
	const at = steps.findIndex((s) => s.id === step.id);
	let nextSteps;
	if (at === -1) nextSteps = steps.length >= TRACE_STEPS_MAX ? steps : [...steps, step];
	else nextSteps = steps.map((s, i) => (i === at ? { ...s, ...step } : s));
	return { ...base, steps: nextSteps, off: false };
}

/** The labels a finished turn keeps for its expandable summary (id, label, status only). */
export const labelSteps = (trace) =>
	(isPlainObject(trace) && Array.isArray(trace.steps) ? trace.steps : [])
		.map(toStep)
		.filter(Boolean)
		.map(({ id, label, status }) => ({ id, label, status }));
